import { useContext, useEffect } from "react";
import GenericFlyout from "../modal/GenericFlyout";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { Badge } from "reactstrap";
import { useMediaQuery } from "react-responsive";
import {
  filterProductsBySkus,
  filterValidSkus,
  getRankedAccessories
} from "../../utils/ProductsUtils";
import { useUserProducts } from "../../hooks/ProductHooks";
import { CartContext } from "../../functional-components/CartContext";
import CockpitProductGrid from "../CockpitLayoutComponents/CockpitProductGrid";
import BjelinButton from "../BjelinButton";
import Cart from "../Cart";
import CartTotal from "../CartTotal";
import { b2bCheckoutUrl, checkoutUrl, shopUrl } from "../../config/urls";
import { useCurrentUser } from "../../functional-components/UserContext";
import COLORS from "../../config/colors";
import Icon from "../Icon";
import { motion } from "motion/react";

const CartDropdown = () => {
  const t = useBjelinTranslations();
  const { b2b } = useCurrentUser();
  const products = useUserProducts();
  const isSmallWidth = useMediaQuery({ maxWidth: 767.98 });
  const isSmallHeight = useMediaQuery({ maxHeight: 767.98 });
  const isSmall = isSmallWidth || isSmallHeight;
  const ua = navigator.userAgent;
  const isSafariMobile =
    ua.indexOf("Safari") > -1 &&
    ua.indexOf("Chrom") === -1 &&
    ua.indexOf("Mobile") === -1;
  const {
    findItem,
    getItems,
    hasItems,
    itemCount,
    getCartIsOpen,
    closeCartDropdown,
    toggleCartDropdown,
    refreshData
  } = useContext(CartContext);
  const showCart = getCartIsOpen();
  const skus = hasItems() ? getItems().map(item => item.sku) : [];
  const filteredProducts = filterProductsBySkus(products, skus);
  //Get the most common accessories related to the cart items
  const accessoriesRanked = getRankedAccessories(filteredProducts);
  const accessoriesToList = filterValidSkus(products, accessoriesRanked).filter(
    sku => !findItem(sku)
  );

  useEffect(() => {
    refreshData(products);
  }, [refreshData, products]);

  return (
    <>
      <motion.button
        id="cart-menu-button"
        title={t("menu.tooltip.cart")}
        aria-haspopup="dialog"
        className="nav-link"
        style={{
          backgroundColor: showCart ? "#fff" : "transparent",
          border: 0,
          paddingRight: 20
        }}
        aria-expanded={showCart}
        onClick={toggleCartDropdown}
        initial={{
          scale: 0.5
        }}
        animate={{
          scale: 1
        }}
        transition={{ type: "spring", stiffness: 500 }}
      >
        <Icon name="cart-shopping" className="icon" />
        {hasItems() && (
          <Badge color="bjelin-orange" style={{ color: "#000" }} pill>
            {itemCount()}
          </Badge>
        )}
      </motion.button>
      <GenericFlyout
        id="cart-dropdown"
        isOpen={showCart}
        close={closeCartDropdown}
        noCloseBtn
        excludeFromClose="cart-menu-button"
        align={"top"}
        ariaLabel={t("cart.heading")}
      >
        {hasItems() ? (
          <div
            style={{
              width: "100%",
              height: isSmall ? "100%" : "fit-content",
              maxHeight: `calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--menu-height) - var(--padding-top) - ${
                isSafariMobile ? 64 : 0
              }px)`,
              display: "flex",
              flexDirection: "column",
              background: "#fff"
            }}
          >
            <div
              style={{
                flex: "1 1 100%",
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                overflowX: "hidden"
              }}
            >
              <div
                style={{ padding: "1.5rem 2rem 0.5rem 15px", flex: "0 0 5rem" }}
              >
                <h2>{t("cart.heading")}</h2>
              </div>
              <div>
                <Cart isCompact={true} />
              </div>
              <CartTotal style={{ padding: "0 15px" }} />
              {!b2b && accessoriesToList.length > 0 && (
                <div
                  style={{
                    background: COLORS.bjelin_light_grey,
                    padding: "15px"
                  }}
                >
                  <h3>{t("cart.recommended.accessories")}</h3>
                  <CockpitProductGrid
                    skus={accessoriesToList}
                    layout={"list"}
                    showCartButtons
                    className="accessories-list"
                  />
                </div>
              )}
            </div>
            <div
              style={{
                height: "4rem",
                minHeight: "4rem",
                display: "flex"
              }}
            >
              <BjelinButton
                id={"cart-dropdown-to-shop"}
                style={{
                  borderRadius: 0,
                  flex: "0 0 40%",
                  padding: "0.5rem 2rem"
                }}
                color={"bjelin-grey"}
                url={shopUrl}
              >
                {t("to.shop")}
              </BjelinButton>
              <BjelinButton
                id={"cart-dropdown-order"}
                style={{
                  borderRadius: 0,
                  flex: "1 1 100%",
                  padding: "0.5rem 2rem"
                }}
                color={"bjelin-green"}
                url={b2b ? b2bCheckoutUrl : checkoutUrl}
                icon={"arrow-right"}
              >
                {t(b2b ? "check.out.b2b" : "check.out")}
              </BjelinButton>
            </div>
          </div>
        ) : (
          <div
            className={"px-4 py-3"}
            style={{
              background: "#fff"
            }}
          >
            <h2>{t("cart.isempty")}</h2>
            <BjelinButton color={"bjelin-green"} url={shopUrl}>
              {t("to.shop")}
            </BjelinButton>
          </div>
        )}
      </GenericFlyout>
    </>
  );
};

export default CartDropdown;
